import type { ColumnType } from 'antd/lib/table'
import dayjs from 'dayjs'
import React, { type CSSProperties } from 'react'
import type { ReturnedTableRow, TableRow } from './formatTableRows'

export type CELL_TYPES =
  | 'dateTime'
  | 'resident'
  | 'actualStock'
  | 'emarResidentMedication'
  | 'carers'
  | 'manageDevice'
  | 'icon'
  | 'log'
  | 'dropdown'
  | 'custom'
  | 'stockLevel'
  | 'center'
  | 'custom'

type BaseColumn<RecordType> = ColumnType<RecordType> & {
  /**
* Toggles whether column is sortable.

If sortable = true, the sortFields object is added to the column object. There are various fields
within the sortFields object that control things like the default sort direction, whether the sort
can be cleared, etc. To see what each field does, see the AntdTable readme.
*/
  sortable?: boolean
  /**
 * Data key used for sorting.

If sortKey is provided, the sorter function will use sortKey rather than key for sorting the various
rows of the table. This is very useful if you want to wrap a value within a div, but still want to sort
by value. You can pass the value as a separate field in the data object, and set sortKey to be the field key.
The table will display the value within a div, but will sort by value. See the notes on sorter in the
AntdTable readme for more info.
*/
  sortKey?: string
  renderTitle?: unknown
  cellType?: CELL_TYPES
  renderCell?: (value: unknown, row: object) => JSX.Element
}
export type Column<RecordType = object> =
  | DateTimeColumn<RecordType>
  | NormalColumn<RecordType>
  | CustomColumn<RecordType>
type CustomColumn<RecordType> = BaseColumn<RecordType> & {
  columnType: 'custom'
}

export const formatTableColumns = <RecordType extends TableRow = TableRow>(
  columns: Column[]
) => {
  const formatedColumns = columns.map((column) => {
    const { columnType } = column

    if (columnType === 'dateTime') {
      return formatDateTimeColumn<RecordType>(column as DateTimeColumn)
    }
    if (columnType === 'custom') {
      return column
    }

    return formatNormalColumn(column as NormalColumn<RecordType>)
  })

  return formatedColumns
}
export type DateTimeColumn<RecordType = unknown> = BaseColumn<RecordType> & {
  columnType: 'dateTime'
}

export const dateTimeColumnSorter = <row extends ReturnedTableRow<TableRow>>(
  row1: { [key in keyof row]: dayjs.Dayjs },
  row2: { [key in keyof row]: dayjs.Dayjs },
  sortKey?: string | keyof row
) => {
  if (sortKey) {
    return (
      dayjs(row1[sortKey], 'DD-MM-YYYY HH:mm').unix() -
      dayjs(row2[sortKey], 'DD-MM-YYYY HH:mm').unix()
    )
  }

  return 1
}

export const DEFAULT_SORT_DIRECTIONS: ColumnType<any>['sortDirections'] = [
  'ascend',
  'descend',
  'ascend'
]
export const formatDateTimeColumn = <RecordType extends object = object>(
  column: DateTimeColumn
) => {
  const { dataIndex, key, renderTitle, sortable, sortKey, title, width } =
    column

  const sortFields: ColumnType<{ [key in keyof RecordType]: dayjs.Dayjs }> = {
    sorter: (row1, row2) => dateTimeColumnSorter(row1, row2, sortKey),
    sortDirections: DEFAULT_SORT_DIRECTIONS,
    defaultSortOrder: 'descend',
    showSorterTooltip: false
  }

  return {
    dataIndex: dataIndex || key,
    key,
    /* We are not maintaning this component anymore, so we don't really care about ts errors anymore */
    /* @ts-ignore */
    title: renderTitle || <div style={styles.title}>{title}</div>,
    ...(width && { width }),
    ...(sortable && sortFields)
  }
}
export type NormalColumn<RecordType = unknown> = BaseColumn<RecordType> & {
  columnType?: 'normal'
  centred?: object
  headerStyle?: object
  key: unknown
  sortType?: 'number' | undefined
}

export const sortNormalRowsFunction = <row extends ReturnedTableRow<TableRow>>(
  row1: row,
  row2: row,
  sortKey: string | undefined | keyof row,
  sortType?: NormalColumn['sortType']
) => {
  if (!sortKey) {
    return 0
  }

  if (sortType === 'number') {
    // Missing values come last
    if (typeof row1[sortKey] !== 'number') return 1
    if (typeof row2[sortKey] !== 'number') return -1

    const value1 = row1[sortKey] as number
    const value2 = row2[sortKey] as number

    return value1 - value2
  }

  // not sure this works the way it should
  // need extra testing here
  // todo
  if ((row1[sortKey] as number) > (row2[sortKey] as number)) return 1
  if (row1[sortKey] === row2[sortKey]) return 0

  return -1
}

export const formatNormalColumn = <RecordType extends TableRow>(
  column: NormalColumn<RecordType>
) => {
  const {
    centred,
    dataIndex,
    headerStyle,
    key,
    renderTitle,
    title,
    sortKey,
    sortable,
    sortType,
    defaultSortOrder
  } = column

  const sortFields: ColumnType<ReturnedTableRow<RecordType>> = {
    sorter: (row1, row2) =>
      sortNormalRowsFunction<ReturnedTableRow<RecordType>>(
        row1,
        row2,
        sortKey,
        sortType
      ),
    sortDirections: ['ascend', 'descend', 'ascend'],
    defaultSortOrder: defaultSortOrder || 'ascend',
    showSorterTooltip: false
  }

  const titleStyles = {
    ...styles.title,
    ...(centred && styles.textCentred),
    ...headerStyle
  }

  return {
    ...column,
    dataIndex: dataIndex || key,
    key,
    title: renderTitle || (
      /* We are not maintaning this component anymore, so we don't really care about ts errors anymore */
      /* @ts-ignore */
      <div style={titleStyles as CSSProperties}>{title}</div>
    ),
    ...(sortable && sortFields)
  }
}

export const styles = {
  textCentred: {
    textAlign: 'center'
  },
  title: {
    lineHeight: '22px'
  }
}
